import { type ReactNode, useState, useRef, useEffect, type ChangeEvent } from "react";

// =========================================================================
// Section: collapsible card holding related fields.
// =========================================================================
export function Section({
  title,
  subtitle,
  children,
  defaultOpen = true,
  rightSlot,
  collapsible = true,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  defaultOpen?: boolean;
  rightSlot?: ReactNode;
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;
  return (
    <div className="bg-bg-card border border-border rounded-lg shadow-card mb-4">
      <button
        type="button"
        onClick={() => collapsible && setOpen(!open)}
        className={`w-full flex items-center justify-between px-5 py-4 text-left ${
          collapsible ? "" : "cursor-default"
        }`}
      >
        <div className="flex-1">
          <h2 className="text-text font-semibold">{title}</h2>
          {subtitle && (
            <p className="text-text-subtle text-xs mt-0.5">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {rightSlot}
          {collapsible && (
            <span className="text-text-subtle text-lg">{open ? "▾" : "▸"}</span>
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-border-subtle">{children}</div>
      )}
    </div>
  );
}

// =========================================================================
// Field: label + help text wrapper.
// =========================================================================
export function Field({
  label,
  required,
  hint,
  children,
}: {
  label?: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm text-text font-medium mb-1.5">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="text-text-subtle text-xs mt-1">{hint}</p>}
    </div>
  );
}

// =========================================================================
// TextInput
// =========================================================================
export function TextInput({
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: "text" | "url" | "email" | "tel" | "number";
  disabled?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-bg-card border border-border rounded-md px-3 py-2 text-text placeholder:text-text-placeholder focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition disabled:bg-bg-subtle disabled:text-text-muted"
    />
  );
}

// =========================================================================
// Textarea
// =========================================================================
export function Textarea({
  value,
  onChange,
  placeholder,
  rows = 4,
  disabled,
  onBlur,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  onBlur?: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-grow: reset to auto so it can shrink, then match content height.
  // Runs on every value change and on mount. We use requestAnimationFrame so
  // the measurement happens after layout (important when the panel was just
  // shown via a tab switch, where scrollHeight can otherwise read stale).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const resize = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    const raf = requestAnimationFrame(resize);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      className="w-full bg-bg-card border border-border rounded-md px-3 py-2 text-text placeholder:text-text-placeholder focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition disabled:bg-bg-subtle disabled:text-text-muted resize-none overflow-hidden"
    />
  );
}

// =========================================================================
// Select
// =========================================================================
export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full bg-bg-card border border-border rounded-md px-3 py-2 text-text focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition disabled:bg-bg-subtle"
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// =========================================================================
// TagPicker: pick up to N tags from a set
// =========================================================================
export function TagPicker({
  options,
  selected,
  onChange,
  max,
}: {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  max?: number;
}) {
  function toggle(option: string) {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      if (max && selected.length >= max) {
        onChange([...selected.slice(1), option]);
      } else {
        onChange([...selected, option]);
      }
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => toggle(option)}
            className={`px-3 py-1.5 rounded-md text-sm border transition ${
              isSelected
                ? "bg-accent text-white border-accent"
                : "bg-bg-card text-text border-border hover:border-border-strong"
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

// =========================================================================
// ChipTextField: free-form removable chips backed by a comma-separated string.
// Used for GBP Secondary Categories — add via input + Enter/comma, remove via ×.
// =========================================================================
export function ChipTextField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const chips = value
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  function commit(next: string[]) {
    onChange(next.join(", "));
  }

  function addChip() {
    const val = draft.trim().replace(/,+$/, "").trim();
    if (!val) return;
    if (chips.some((c) => c.toLowerCase() === val.toLowerCase())) {
      setDraft("");
      return;
    }
    commit([...chips, val]);
    setDraft("");
  }

  function removeChip(idx: number) {
    commit(chips.filter((_, i) => i !== idx));
  }

  return (
    <div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {chips.map((chip, i) => (
            <span
              key={`${chip}-${i}`}
              className="inline-flex items-center gap-1 bg-bg-card border border-border rounded-full pl-2.5 pr-1.5 py-1 text-xs text-text"
            >
              {chip}
              <button
                type="button"
                onClick={() => removeChip(i)}
                className="text-text-subtle hover:text-danger leading-none"
                aria-label={`Remove ${chip}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addChip();
            }
          }}
          placeholder={placeholder ?? "Add…"}
          className="flex-1 bg-bg-card border border-border rounded-md px-2.5 py-1.5 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-accent"
        />
        <button
          type="button"
          onClick={addChip}
          className="bg-accent text-white text-sm font-medium rounded-md px-3 py-1.5 hover:opacity-90"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// Button
// =========================================================================
export function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled,
  size = "md",
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "danger" | "ghost";
  disabled?: boolean;
  size?: "sm" | "md";
  /** chunk3z-fix421: extra classes for layout tweaks at the call site, e.g.
   *  keeping a label on one line when the button sits in a tight flex row. */
  className?: string;
}) {
  const variantClasses = {
    primary: "bg-accent hover:bg-accent-hover text-white",
    secondary: "bg-bg-card hover:bg-bg-hover border border-border text-text",
    danger: "bg-danger hover:bg-danger-hover text-white",
    ghost: "bg-transparent hover:bg-bg-hover text-text",
  }[variant];

  const sizeClasses = {
    sm: "text-xs px-2.5 py-1",
    md: "text-sm px-3 py-1.5",
  }[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${variantClasses} ${sizeClasses} font-medium rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

// =========================================================================
// ColorPicker: native color input + hex display
// =========================================================================
export function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-10 h-10 rounded-md border border-border overflow-hidden shrink-0">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full cursor-pointer border-0 p-0"
          style={{ backgroundColor: value }}
        />
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="bg-bg-card border border-border rounded-md px-3 py-2 text-text font-mono text-sm w-32 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition"
      />
    </div>
  );
}

// =========================================================================
// CheckboxGrid: grid of checkable items with optional select-all/none.
// Used for External Link Domains.
// =========================================================================
export function CheckboxGrid({
  items,
  selected,
  onChange,
  columns = 3,
}: {
  items: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  columns?: 2 | 3 | 4;
}) {
  function toggle(item: string) {
    const next = new Set(selected);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    onChange(next);
  }

  const colClass = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" }[columns];

  return (
    <div className={`grid grid-cols-2 ${colClass} gap-y-1.5 gap-x-4`}>
      {items.map((item) => {
        const isChecked = selected.has(item);
        return (
          <label
            key={item}
            className="flex items-center gap-2 text-sm text-text cursor-pointer hover:text-text"
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggle(item)}
              className="w-4 h-4 rounded border-border text-accent focus:ring-2 focus:ring-accent/20"
            />
            <span className={isChecked ? "" : "text-text-muted"}>{item}</span>
          </label>
        );
      })}
    </div>
  );
}

// =========================================================================
// RepeaterList: dynamic list of items where each has form fields.
// Used for CTA Buttons, Trust Badges.
// =========================================================================
export function RepeaterList<T>({
  items,
  onChange,
  max,
  renderItem,
  blank,
  addLabel = "Add",
}: {
  items: T[];
  onChange: (next: T[]) => void;
  max?: number;
  renderItem: (item: T, index: number, update: (v: T) => void) => ReactNode;
  blank: () => T;
  addLabel?: string;
}) {
  function add() {
    if (max && items.length >= max) return;
    onChange([...items, blank()]);
  }

  function remove(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function update(index: number, value: T) {
    onChange(items.map((it, i) => (i === index ? value : it)));
  }

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">{renderItem(item, i, (v) => update(i, v))}</div>
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-text-subtle hover:text-danger transition px-2 py-2 text-sm"
            title="Remove"
          >
            ✕
          </button>
        </div>
      ))}
      {(!max || items.length < max) && (
        <button
          type="button"
          onClick={add}
          className="text-accent hover:text-accent-hover text-sm font-medium transition"
        >
          + {addLabel}
        </button>
      )}
    </div>
  );
}
